import { useEffect } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { t } from 'i18next'
import { FormProvider, useForm } from 'react-hook-form'

import { stepsFlow } from '@/features/proposta/brasil/domain/Comprador/steps'
import type { TPropostaSchemaInput } from '@/features/proposta/brasil/domain/schemas'
import {
  type CompradorPessoaFisicaStepSchemaInput,
  type CompradorPessoaFisicaStepSchemaOutput,
} from '@/features/proposta/brasil/domain/schemas/compradores/comprador-pf.schema'
import type { TClienteCompleto } from '@/features/proposta/brasil/hooks/use-orquestrar-criacao-edicao'
import { useStepManager } from '@/features/proposta/brasil/hooks/use-stepper-proposta'
import { ContatosComprador } from '@/features/proposta/brasil/pages/criar-proposta/etapa-comprador/cadastro-comprador/contatos-comprador'
import { DadosMaePai } from '@/features/proposta/brasil/pages/criar-proposta/etapa-comprador/cadastro-comprador/dados-mae-pai'
import { EstadoCivilVinculo } from '@/features/proposta/brasil/pages/criar-proposta/etapa-comprador/cadastro-comprador/estado-civil-vinculo'
import { VincularCompradorMenor } from '@/features/proposta/brasil/pages/criar-proposta/etapa-comprador/cadastro-comprador/vincular-comprador-menor'
import type { TCompradoresCadastrados } from '@/features/proposta/brasil/types/proposta.type'
import { StepCompradorLayout } from '@/features/proposta/shared/components/etapa-comprador/step-comprador-layout'
import type { TNacionalidade, TProfissao } from '@/types/cliente'
import { origemTelefone } from '@/utils/schemas/telefone.schema'

type TCadastroMaePaiFluxoIndependenteProps = {
  onCancel: () => void
  onSubmit: (data: CompradorPessoaFisicaStepSchemaOutput, isDirty: boolean) => Promise<void>
  compradores: TCompradoresCadastrados
  clientes: TPropostaSchemaInput['cadastroCompradores']['clientes']
  nacionalidades: TNacionalidade[]
  profissoes: TProfissao[]
  clienteToEdit: TClienteCompleto | null
  emailsJaVinculados: Record<string, string>
}
export const CadastroMaePaiFluxoIndependente = ({
  onCancel,
  onSubmit,
  compradores,
  nacionalidades,
  profissoes,
  clienteToEdit,
  clientes,
  emailsJaVinculados,
}: TCadastroMaePaiFluxoIndependenteProps) => {
  const { schema, conditionalSteps } = stepsFlow.compradorPessoaFisica
  const form = useForm<CompradorPessoaFisicaStepSchemaInput, unknown, CompradorPessoaFisicaStepSchemaOutput>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      fluxoIndependente: 'MaePai',
      contato: {
        email: '',
        telefone: {
          origem: origemTelefone.Brasil,
          numero: '',
          principal: true,
        },
      },
      dados: {
        buscouCpf: false,
        cpf: '',
        nacionalidadeId: nacionalidades[0] ? nacionalidades[0].id : '',
        profissaoId: '',
        email: '',
        id: '',
        maiorDe18Anos: '',
        nome: '',
      },
      estadoCivil: {
        status: undefined,
        tipoAssinatura: null,
        regimeCasamento: null,
        uniaoEstavel: null,
      },
      imagem: null,
      tipoDoCliente: 'Contato',
      tipo: 'PessoaFisica',
      mode: 'create',
      maePai: null,
      conjuge: null,
    },
  })

  const isDirty = form.formState.isDirty
  const { steps, idx, onNext, onFinish, onBack, isLast } = useStepManager(
    conditionalSteps,
    () => form.getValues(),
    onCancel,
    onSubmit,
    isDirty,
  )
  const currentStep = steps[idx]

  useEffect(() => {
    if (clienteToEdit) {
      form.reset({
        emailsJaVinculados,
        fluxoIndependente: 'MaePai',
        conjuge: null,
        estadoCivil: null,
        mode: 'edit',
        tipoDoCliente: 'Contato',
        tipo: 'PessoaFisica',
        contato: null,
        dados: {
          buscouCpf: true,
          cpf: clienteToEdit.documento,
          email: clienteToEdit.email,
          id: clienteToEdit.id,
          nacionalidadeId: clienteToEdit.nacionalidadeId,
          profissaoId: clienteToEdit.profissaoId,
          maiorDe18Anos: 'nao',
          nome: clienteToEdit.nome,
        },
        maePai: {
          emailsJaVinculados,
          imagem: clienteToEdit.vinculo?.imagem,
          estadoCivil: {
            regimeCasamento: clienteToEdit.vinculo?.regimeCasamento,
            uniaoEstavel: clienteToEdit.vinculo?.uniaoEstavel,
            status: clienteToEdit.vinculo?.estadoCivil,
            tipoAssinatura: clienteToEdit.vinculo?.tipoAssinatura,
          },
          tipo: 'MaePai',
          dados: {
            buscouCpf: true,
            cpf: clienteToEdit.vinculo?.documento,
            nacionalidadeId: clienteToEdit.vinculo?.nacionalidadeId,
            profissaoId: clienteToEdit.vinculo?.profissaoId,
            email: clienteToEdit.vinculo?.email,
            id: clienteToEdit.vinculo?.id,
            maiorDe18Anos: clienteToEdit.vinculo?.maiorDe18Anos ? 'sim' : 'nao',
            nome: clienteToEdit.vinculo?.nome,
          },
          contato: {
            email: clienteToEdit.vinculo?.email,
            telefone: {
              origem: clienteToEdit.vinculo?.telefone.origem,
              numero: clienteToEdit.vinculo?.telefone.numero,
              principal: true,
            },
          },
        },
      })
    }
  }, [])

  const renderContent = () => {
    switch (currentStep) {
      case 'vincularCompradorMenor':
        return (
          <VincularCompradorMenor
            onBack={onBack}
            onNext={onNext}
            compradores={compradores}
            clientes={clientes}
            emailsJaVinculados={emailsJaVinculados}
          />
        )
      case 'dadosMaePai':
        return (
          <DadosMaePai
            onBack={onBack}
            onNext={onNext}
            nacionalidades={nacionalidades}
            compradores={compradores}
            profissoes={profissoes}
          />
        )
      case 'contatosMaePai':
        return (
          <ContatosComprador
            titulo={t('proposta:cadastro_compradores.steps.contatos.mae_pai_titulo')}
            fieldName="maePai.contato"
            isLast={isLast}
            onFinish={(e) => onFinish(e as CompradorPessoaFisicaStepSchemaOutput)}
            onNext={onNext}
            onBack={onBack}
          />
        )
      case 'estadoCivilMaePai':
        return (
          <EstadoCivilVinculo
            fieldPath="maePai.estadoCivil"
            isLast={isLast}
            onNext={onNext}
            onBack={onBack}
            onFinish={(e) => onFinish(e as CompradorPessoaFisicaStepSchemaOutput)}
          />
        )
      default:
        return null
    }
  }

  return (
    <FormProvider {...form}>
      <StepCompradorLayout steps={steps} current={idx}>
        {renderContent()}
      </StepCompradorLayout>
    </FormProvider>
  )
}
